import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Divider,
  Typography,
  Alert,
  Grid,
  Paper,
  IconButton,
} from '@mui/material';
import { Add, Delete, DragIndicator } from '@mui/icons-material';

import { MultipleChoiceAction, MultipleChoiceOption } from '../../../types';

import MultipleChoicePreviewSection from './MultipleChoicePreviewSection';

interface MultipleChoiceActionEditorProps {
  action: MultipleChoiceAction;
  onChange: (action: MultipleChoiceAction) => void;
  contextKeys: string[];
}

const MultipleChoiceActionEditor: React.FC<MultipleChoiceActionEditorProps> = ({
  action,
  onChange,
  contextKeys,
}) => {
  const [newChoiceText, setNewChoiceText] = useState('');

  const handleChoiceIdChange = (choiceId: string) => {
    onChange({
      ...action,
      data: {
        ...action.data,
        choiceId,
      },
    });
  };

  const handleMinSelectionsChange = (minSelections: string) => {
    const value = parseInt(minSelections, 10);
    onChange({
      ...action,
      data: {
        ...action.data,
        minSelections: Number.isNaN(value) ? undefined : Math.max(0, value),
      },
    });
  };

  const handleMaxSelectionsChange = (maxSelections: string) => {
    const value = parseInt(maxSelections, 10);
    onChange({
      ...action,
      data: {
        ...action.data,
        maxSelections: Number.isNaN(value) ? undefined : Math.max(0, value),
      },
    });
  };

  const handleSubmitButtonTextChange = (submitButtonText: string) => {
    onChange({
      ...action,
      data: {
        ...action.data,
        submitButtonText: submitButtonText || undefined,
      },
    });
  };

  const handleChoiceChange = (index: number, choice: MultipleChoiceOption) => {
    const newChoices = [...action.data.choices];
    newChoices[index] = choice;
    onChange({
      ...action,
      data: {
        ...action.data,
        choices: newChoices,
      },
    });
  };

  const handleChoiceDelete = (index: number) => {
    const newChoices = action.data.choices.filter((_, i) => i !== index);
    onChange({
      ...action,
      data: {
        ...action.data,
        choices: newChoices,
      },
    });
  };

  const handleAddChoice = () => {
    if (!newChoiceText.trim()) {
      return;
    }

    const sanitizedValue = newChoiceText.trim().toLowerCase().replace(/\s+/g, '_');
    const newChoice: MultipleChoiceOption = {
      text: newChoiceText.trim(),
      value: sanitizedValue,
    };

    onChange({
      ...action,
      data: {
        ...action.data,
        choices: [...action.data.choices, newChoice],
      },
    });
    setNewChoiceText('');
  };

  const hasChoices = action.data.choices.length > 0;
  const hasValidChoices = action.data.choices.every((choice) => choice.text && choice.value);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Multiple Choice Action Configuration
      </Typography>

      {/* Configuration Section */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12}>
          <TextField
            label="Context Key (choiceId)"
            value={action.data.choiceId || ''}
            onChange={(e) => handleChoiceIdChange(e.target.value)}
            fullWidth
            required
            helperText="The key where the selected values array will be stored in context"
          />
          {contextKeys.length > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Available context keys: {contextKeys.join(', ')}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Minimum Selections"
            type="number"
            value={action.data.minSelections || ''}
            onChange={(e) => handleMinSelectionsChange(e.target.value)}
            fullWidth
            inputProps={{ min: 0 }}
            helperText="Minimum choices user must select (default: 1)"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Maximum Selections"
            type="number"
            value={action.data.maxSelections || ''}
            onChange={(e) => handleMaxSelectionsChange(e.target.value)}
            fullWidth
            inputProps={{ min: 0 }}
            helperText="Maximum choices user can select (optional)"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Submit Button Text"
            value={action.data.submitButtonText || ''}
            onChange={(e) => handleSubmitButtonTextChange(e.target.value)}
            fullWidth
            placeholder="Continue"
            helperText="Text for the submit button (default: 'Continue')"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Choices Section */}
      <Typography variant="h6" gutterBottom>
        Choice Options
      </Typography>

      {!hasChoices && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No choice options defined yet. Add at least one choice option.
        </Alert>
      )}

      {/* Choice Items */}
      {action.data.choices.map((choice, index) => (
        <Paper key={`${choice.text}-${choice.value}`} elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton size="small" sx={{ cursor: 'grab' }}>
              <DragIndicator />
            </IconButton>

            <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: 80 }}>
              Choice {index + 1}
            </Typography>

            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Display Text"
                  value={choice.text}
                  onChange={(e) => handleChoiceChange(index, { ...choice, text: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Value"
                  value={choice.value}
                  onChange={(e) => handleChoiceChange(index, { ...choice, value: e.target.value })}
                  fullWidth
                  required
                  helperText="The value stored in the context array"
                />
              </Grid>
            </Grid>

            <IconButton
              size="small"
              onClick={() => handleChoiceDelete(index)}
              color="error"
              disabled={action.data.choices.length <= 1}
            >
              <Delete />
            </IconButton>
          </Box>
        </Paper>
      ))}

      {/* Add New Choice */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          label="New Choice Text"
          value={newChoiceText}
          onChange={(e) => setNewChoiceText(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleAddChoice();
            }
          }}
          placeholder="e.g., 'Education'"
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleAddChoice}
          startIcon={<Add />}
          disabled={!newChoiceText.trim()}
        >
          Add Choice
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Preview Section */}
      <MultipleChoicePreviewSection
        choices={action.data.choices}
        minSelections={action.data.minSelections}
        maxSelections={action.data.maxSelections}
        submitButtonText={action.data.submitButtonText}
      />

      {/* Validation Warnings */}
      {hasChoices && !hasValidChoices && (
        <Box mt={2}>
          <Alert severity="warning">
            All choices must have both display text and value to work properly.
          </Alert>
        </Box>
      )}

      {action.data.minSelections &&
        action.data.maxSelections &&
        action.data.minSelections > action.data.maxSelections && (
          <Box mt={2}>
            <Alert severity="error">
              Minimum selections cannot be greater than maximum selections.
            </Alert>
          </Box>
        )}
    </Box>
  );
};

export default MultipleChoiceActionEditor;
